import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const ThreeBackground = () => {
  const mountRef = useRef(null);
  const frameId = useRef(null);

  useEffect(() => {
    try {
      // Scene setup
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setClearColor(0x000000, 0);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      
      // Store current mount ref to avoid cleanup issues
      const currentMount = mountRef.current;
      
      if (currentMount) {
        currentMount.appendChild(renderer.domElement);
      }

      // Lighting
      const ambientLight = new THREE.AmbientLight(0x404040, 0.4);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 10, 5);
      directionalLight.castShadow = true;
      scene.add(directionalLight);

      // Optimized Particle system (reduced from 2000 to 800)
      const particleGeometry = new THREE.BufferGeometry();
      const particleCount = 800;
      const particlePositions = new Float32Array(particleCount * 3);
      const particleColors = new Float32Array(particleCount * 3);

      for (let i = 0; i < particleCount * 3; i += 3) {
        particlePositions[i] = (Math.random() - 0.5) * 120;
        particlePositions[i + 1] = (Math.random() - 0.5) * 120;
        particlePositions[i + 2] = (Math.random() - 0.5) * 120;

        // Brighter crypto-style colors: purple, blue, cyan, pink
        const color = new THREE.Color();
        const hue = Math.random() * 0.4 + 0.6; // Purple to cyan range
        color.setHSL(hue, 1.0, 0.7); // Higher saturation and lightness
        particleColors[i] = color.r;
        particleColors[i + 1] = color.g;
        particleColors[i + 2] = color.b;
      }

      particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
      particleGeometry.setAttribute('color', new THREE.BufferAttribute(particleColors, 3));

      const particleMaterial = new THREE.PointsMaterial({
        size: 1.2,
        vertexColors: true,
        transparent: true,
        opacity: 0.8,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true
      });

      const particles = new THREE.Points(particleGeometry, particleMaterial);
      scene.add(particles);

      // Reduced Geometric shapes (from 25 to 12)
      const shapes = [];
      const geometries = [
        new THREE.OctahedronGeometry(1),
        new THREE.TetrahedronGeometry(1),
        new THREE.IcosahedronGeometry(1),
        new THREE.DodecahedronGeometry(1)
      ];

      for (let i = 0; i < 12; i++) {
        const geometry = geometries[Math.floor(Math.random() * geometries.length)];
        const isWireframe = Math.random() > 0.6;
        const material = new THREE.MeshLambertMaterial({
          color: new THREE.Color().setHSL(Math.random() * 0.4 + 0.6, 1.0, 0.6),
          transparent: true,
          opacity: isWireframe ? 0.7 : 0.35,
          wireframe: isWireframe,
          emissive: new THREE.Color().setHSL(Math.random() * 0.4 + 0.6, 0.5, 0.1)
        });

        const shape = new THREE.Mesh(geometry, material);
        shape.position.set(
          (Math.random() - 0.5) * 70,
          (Math.random() - 0.5) * 70,
          (Math.random() - 0.5) * 70
        );
        shape.rotation.set(
          Math.random() * Math.PI,
          Math.random() * Math.PI,
          Math.random() * Math.PI
        );
        shape.scale.setScalar(Math.random() * 2.5 + 1);
        
        shapes.push(shape);
        scene.add(shape);
      }

      // Reduced Floating rings (from 8 to 5)
      const rings = [];
      for (let i = 0; i < 5; i++) {
        const ringGeometry = new THREE.TorusGeometry(3.5, 0.15, 8, 100);
        const ringMaterial = new THREE.MeshLambertMaterial({
          color: new THREE.Color().setHSL(0.7 + Math.random() * 0.3, 1.0, 0.7),
          transparent: true,
          opacity: 0.6,
          emissive: new THREE.Color().setHSL(0.7 + Math.random() * 0.3, 0.7, 0.25)
        });

        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.position.set(
          (Math.random() - 0.5) * 50,
          (Math.random() - 0.5) * 50,
          (Math.random() - 0.5) * 50
        );
        ring.rotation.set(
          Math.random() * Math.PI,
          Math.random() * Math.PI,
          Math.random() * Math.PI
        );
        
        rings.push(ring);
        scene.add(ring);
      }

      // Add console log to verify Three.js is working
      console.log('Three.js background optimized with:', {
        particles: particleCount,
        shapes: shapes.length,
        rings: rings.length,
        renderer: renderer
      });

      // Camera position
      camera.position.z = 30;

      // Animation loop
      const animate = () => {
        frameId.current = requestAnimationFrame(animate);

        // Rotate particles (slower for performance)
        particles.rotation.x += 0.0003;
        particles.rotation.y += 0.0005;

        // Animate shapes (reduced frequency)
        shapes.forEach((shape, index) => {
          shape.rotation.x += 0.008 * (index % 2 === 0 ? 1 : -1);
          shape.rotation.y += 0.008 * (index % 3 === 0 ? 1 : -1);
          shape.rotation.z += 0.004 * (index % 4 === 0 ? 1 : -1);
          
          // Floating motion (reduced amplitude)
          shape.position.y += Math.sin(Date.now() * 0.001 + index) * 0.015;
        });

        // Animate rings (slower rotation)
        rings.forEach((ring, index) => {
          ring.rotation.x += 0.008 * (index % 2 === 0 ? 1 : -1);
          ring.rotation.y += 0.012 * (index % 3 === 0 ? 1 : -1);
          ring.rotation.z += 0.006 * (index % 4 === 0 ? 1 : -1);
        });

        // Gentler camera movement
        camera.position.x = Math.sin(Date.now() * 0.0003) * 3;
        camera.position.y = Math.cos(Date.now() * 0.0002) * 2;
        camera.lookAt(0, 0, 0);

        renderer.render(scene, camera);
      };

      animate();

      // Handle window resize
      const handleResize = () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      };

      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        if (frameId.current) {
          cancelAnimationFrame(frameId.current);
        }
        window.removeEventListener('resize', handleResize);
        if (currentMount && renderer.domElement) {
          currentMount.removeChild(renderer.domElement);
        }
        renderer.dispose();
      };
    } catch (error) {
      console.error('Three.js initialization failed:', error);
    }
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
        background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%), linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 50%, #16213e 100%)'
      }}
    />
  );
};

export default ThreeBackground; 